import clsx from "clsx";
import { FC, ReactNode } from "react";

import { BillingAccountChip } from "@/components/Portal/Documents/BillingAccounts/BillingAccountChip";
import {
  getBillingAccountName,
  getBillingAccountNumber,
} from "@/components/Portal/Documents/BillingAccounts/billingAccountUtils";
import { DrAccount } from "@/services/model";

type SummaryVariant = "default" | "inverted";

const variantMapping = {
  default: {
    chipTone: "default",
    name: "text-foreground-primary",
    meta: "text-foreground-tertiary",
  },
  inverted: {
    chipTone: "inverted",
    name: "text-foreground-primary-inverse",
    meta: "text-foreground-tertiary-inverse",
  },
} as const satisfies Record<SummaryVariant, object>;

interface Props {
  account: DrAccount;
  /** Shown at the end of the name row, e.g. the Abo count. */
  aside?: ReactNode;
  variant?: SummaryVariant;
}

/** Number, name, Referenz and E-Mail of a Vertragskonto in one block. */
export const BillingAccountSummary: FC<Props> = ({
  account,
  aside,
  variant = "default",
}) => {
  const variantClasses = variantMapping[variant];

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      <div className="flex flex-col items-start gap-2 tablet:flex-row tablet:items-center tablet:gap-4">
        <div className="flex w-full min-w-0 items-center gap-2 tablet:w-auto tablet:flex-1">
          {account.billingAccount && (
            <BillingAccountChip
              text={getBillingAccountNumber(account)}
              tone={variantClasses.chipTone}
            />
          )}
          <p
            className={clsx(
              "truncate text-base font-semibold leading-md",
              variantClasses.name,
            )}
          >
            {getBillingAccountName(account)}
          </p>
        </div>
        {aside}
      </div>
      <div
        className={clsx(
          "flex min-w-0 flex-col gap-1 text-sm leading-md tablet:flex-row tablet:gap-3",
          variantClasses.meta,
        )}
      >
        {account.billingReference && (
          <p className="truncate">{account.billingReference}</p>
        )}
        {account.billingEmail && (
          <p className="truncate">{account.billingEmail}</p>
        )}
      </div>
    </div>
  );
};
